// London Smoke & Cure — canonical wholesale pricing tier resolution (Firestore-first).
//
// Contract:
// - users/{uid}.Pricing (PascalCase) is the single write field for tier assignment.
// - Product rows live in Firestore collections products_{key} (key lowercased).
// - Registry / labels: pricing_sheets/{key} (written by Product Management).
// - Google Sheets CSV is NOT a runtime catalogue source (import/seed only in Product Management).
// - Pure key helpers live in customerPricingKey.js (safe for tests / schedule utils without Auth).

import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { isCatalogVisibleToCustomer } from '../utils/productCatalogVisibility';
import {
    STANDARD_PRICING_KEYS,
    MASTER_CATALOG_PRICING_KEY,
    DEFAULT_WHOLESALE_PRICING_KEY,
    GUEST_PRICING_KEY,
    normalizePricingKey,
    coerceCustomerPricingKey,
    formatCustomerPricingLabel,
    resolvePricingKeyFromUserData,
} from '../utils/customerPricingKey';

export {
    STANDARD_PRICING_KEYS,
    MASTER_CATALOG_PRICING_KEY,
    DEFAULT_WHOLESALE_PRICING_KEY,
    GUEST_PRICING_KEY,
    normalizePricingKey,
    coerceCustomerPricingKey,
    formatCustomerPricingLabel,
    resolvePricingKeyFromUserData,
};

export const PRICING_SHEETS_COLLECTION = 'pricing_sheets';
export const PRODUCTS_COLLECTION_PREFIX = 'products_';

/**
 * @param {string} uid
 * @returns {Promise<string>} normalized pricing key
 */
export async function resolvePricingKeyForUserId(uid) {
    if (!uid) return resolvePricingKeyFromUserData(null);
    try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (!snap.exists()) return resolvePricingKeyFromUserData(null);
        return resolvePricingKeyFromUserData(snap.data());
    } catch (error) {
        console.warn('[pricingTierService] resolvePricingKeyForUserId failed:', error);
        return resolvePricingKeyFromUserData(null);
    }
}

/** @param {string} pricingKey — normalized lowercase */
export function productsCollectionName(pricingKey) {
    return `${PRODUCTS_COLLECTION_PREFIX}${normalizePricingKey(pricingKey)}`;
}

/** Custom = not one of the four standard built-in keys. */
export function isCustomPricingKey(pricingKey) {
    return !STANDARD_PRICING_KEYS.includes(normalizePricingKey(pricingKey));
}

/**
 * Load raw product rows from Firestore products_{key}.
 * @param {string} pricingKey
 * @param {{ catalogVisibleOnly?: boolean }} [options] — when true, omit fully muted products (customer catalogue; muted+acceptEnquiries still included)
 * @returns {Promise<{ rows: object[], pricingKey: string, collectionName: string, error: string|null }>}
 */
export async function loadFirestorePricingRows(pricingKey, options = {}) {
    const key = normalizePricingKey(pricingKey);
    const collectionName = productsCollectionName(key);
    const { catalogVisibleOnly = false } = options;

    try {
        const snap = await getDocs(collection(db, collectionName));
        const rows = [];
        snap.forEach((d) => {
            const data = d.data() || {};
            if (!data.Code) data.Code = d.id;
            rows.push(data);
        });
        let valid = rows.filter(
            (p) => (p?.Name || '').toString().trim() && (p?.Price ?? '').toString().trim()
        );
        if (catalogVisibleOnly) {
            valid = valid.filter((p) => isCatalogVisibleToCustomer(p));
        }
        if (!valid.length) {
            return {
                rows: [],
                pricingKey: key,
                collectionName,
                error: `No products in Firestore collection "${collectionName}".`,
            };
        }
        return { rows: valid, pricingKey: key, collectionName, error: null };
    } catch (error) {
        console.warn(`[pricingTierService] loadFirestorePricingRows(${collectionName}) failed:`, error);
        return {
            rows: [],
            pricingKey: key,
            collectionName,
            error: error?.message || String(error),
        };
    }
}

/**
 * Labels for admin UI — pricing_sheets first, else uppercase key.
 * @returns {Promise<Array<{ key: string, label: string }>>}
 */
export async function listRegisteredPricingTiers() {
    try {
        const snap = await getDocs(collection(db, PRICING_SHEETS_COLLECTION));
        const tiers = [];
        snap.forEach((d) => {
            const id = (d.id || '').trim();
            if (!id) return;
            const data = d.data() || {};
            tiers.push({
                key: id.toLowerCase(),
                label: data.label ? String(data.label) : id,
            });
        });
        tiers.sort((a, b) => {
            if (a.key === 'default') return -1;
            if (b.key === 'default') return 1;
            return a.label.localeCompare(b.label);
        });
        return tiers;
    } catch (error) {
        console.warn('[pricingTierService] listRegisteredPricingTiers failed:', error);
        return STANDARD_PRICING_KEYS.map((key) => ({
            key,
            label: key === 'default' ? 'Default' : key.toUpperCase(),
        }));
    }
}
